import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { LockOpenIcon, Edit, Trash2, UserPlus, Lock } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { getRequest, postRequest, deleteRequest, putRequest } from "../../ApiFunction";
import API, { BASE_URL } from "../../Api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [formErrors, setFormErrors] = useState({});

  const validateForm = (isEdit = false) => {
    const errors = {};
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
    if (!isEdit && !formData.password) errors.password = 'Password is required';
    else if (!isEdit && formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (!formData.role) errors.role = 'Role is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ username: '', email: '', password: '', role: 'user' });
    setFormErrors({});
    setSelectedUser(null);
  };

  // Create User
  const handleCreateUser = async () => {
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      const result = await postRequest(`${BASE_URL}/admin/create_user`, formData);
      
      if (result.data.success) {
        toast.success('User created successfully');
        setCreateDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        toast.error(result.data.message || 'Failed to create user');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating user');
    } finally {
      setSaving(false);
    }
  };

  // Edit User
  const handleEditUser = async () => {
    if (!validateForm(true)) return;
    
    try {
      setSaving(true);
      const result = await putRequest(`${BASE_URL}/admin/update_user/${selectedUser.id}`, {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        ...(formData.password && { password: formData.password }),
      });
      
      if (result.data.success) {
        toast.success('User updated successfully');
        setEditDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        toast.error(result.data.message || 'Failed to update user');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating user');
    } finally {
      setSaving(false);
    }
  };

  // Delete User
  const handleDeleteUser = async () => {
    try {
      setSaving(true);
      const result = await deleteRequest(`${BASE_URL}/admin/delete_user/${selectedUser.id}`);
      
      if (result.data.success) {
        toast.success('User deleted successfully');
        setDeleteDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        toast.error(result.data.message || 'Failed to delete user');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting user');
    } finally {
      setSaving(false);
    }
  };

  // Block/Unblock User
  const handleToggleBlock = async (user) => {
    try {
      const result = await postRequest(`${BASE_URL}${API.BLOCK_UNBLOCK_USER}`, { 
        id: user.id,
        isBlocked: !user.isBlocked 
      });
      
      if (result.data.success) {
        toast.success(user.isBlocked ? 'User unblocked successfully' : 'User blocked successfully');
        fetchUsers();
      } else {
        toast.error(result.data.message || 'Failed to update user status');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating user status');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "username", headerName: "Username", width: 150 },
    { field: "email", headerName: "Email", width: 220 },
    { 
      field: "role", 
      headerName: "Role", 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'admin' ? 'primary' : 'default'}
          size="small"
        />
      )
    },
    {
      field: "isBlocked",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Blocked' : 'Active'} 
          color={params.value ? 'error' : 'success'}
          size="small"
        />
      )
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
      valueGetter: (params) =>
        new Date(params.row.createdAt).toLocaleString(),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div className="d-flex justify-content-center gap-2 align-items-center">
          <IconButton
            size="small"
            color={params.row.isBlocked ? "success" : "warning"}
            onClick={() => handleToggleBlock(params.row)}
            title={params.row.isBlocked ? "Unblock User" : "Block User"}
          >
            {params.row.isBlocked ? <LockOpenIcon size={18} /> : <Lock size={18} />}
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => openEditDialog(params.row)}
            title="Edit User"
          >
            <Edit size={18} />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => openDeleteDialog(params.row)}
            title="Delete User"
          >
            <Trash2 size={18} />
          </IconButton>
        </div>
      ),
    },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getRequest(`${BASE_URL}${API.GET_ALL_USERS}`);

      const { success, data, message } = result.data;

      if (!success) {
        toast.error(message || "Failed to fetch users");
        return;
      }

      // Map the API response to match the DataGrid format
      const formattedUsers = data?.users?.map((user) => ({
        id: user._id,
        username: user.username,
        email: user.email,
        role: user.role,
        isBlocked: user.isBlocked || false,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
      })) || [];

      setUsers(formattedUsers);
    } catch (err) {
      toast.error("Error fetching users: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const rows = users;
  const paginationModel = { page: 0, pageSize: 10 };

  return (
    <div className="container">
      <div className="row">
        <div className="col-xs-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">User Management</h5>
              <p className="text-muted mb-0">
                Manage users, their roles, status, and permissions.
              </p>
            </div>
            <Button
              variant="contained"
              startIcon={<UserPlus size={18} />}
              onClick={() => {
                resetForm();
                setCreateDialogOpen(true);
              }}
            >
              Add User
            </Button>
          </div>
          <Paper className="custom-paper">
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{ pagination: { paginationModel } }}
              pageSizeOptions={[5, 10, 25]}
              loading={loading}
              sx={{ border: 0 }}
            />
          </Paper>
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={!!formErrors.username}
              helperText={formErrors.username}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password}
            />
            <FormControl fullWidth error={!!formErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={!!formErrors.username}
              helperText={formErrors.username}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <TextField
              label="Password (leave blank to keep current)"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password || 'Leave blank to keep current password'}
            />
            <FormControl fullWidth error={!!formErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{selectedUser?.username}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;
